import { useQuery } from "@tanstack/react-query";
import {
  buildIlikeOrFilter,
  filterIn,
  filterSearch,
  orFilter,
} from "@west-tennessee-consulting/supabase/postgrest-filter";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { Communications } from "@/src/gql/graphql";

/**
 * Paginated communications hook with field-specific search parameters
 * Enables server-side filtering and pagination for the communications table
 *
 * Supports:
 * - Field-specific searches: titleSearch, bodySearch, contactSearch
 * - Type and direction filtering
 * - Date range filtering on occurred_at
 * - Pagination with total count
 */
export function useCommunicationsPaginated(params?: {
  page?: number;
  limit?: number;
  search?: string;
  titleSearch?: string;
  bodySearch?: string;
  contactSearch?: string;
  typeSearch?: string;
  direction?: "inbound" | "outbound";
  status?: string | string[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
}) {
  return useQuery({
    queryKey: ["communications-paginated", params],
    // Modest window: not every write path invalidates this key (a create only
    // invalidates the bare list key), so focus/mount refetch stays on — it just
    // no longer fires on EVERY mount and focus when the data is seconds old.
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      const supabase = getSupabase();

      // Build base query for filtering
      let countQuery = supabase.from("communications").select("*", { count: "exact", head: true });
      let dataQuery = supabase.from("communications").select("*");

      // Apply exact match filters
      if (params?.direction) {
        countQuery = countQuery.eq("direction", params.direction);
        dataQuery = dataQuery.eq("direction", params.direction);
      }

      if (params?.status) {
        if (Array.isArray(params.status) && params.status.length > 0) {
          countQuery = countQuery.in("status", params.status);
          dataQuery = dataQuery.in("status", params.status);
        } else if (typeof params.status === "string") {
          countQuery = countQuery.eq("status", params.status);
          dataQuery = dataQuery.eq("status", params.status);
        }
      }

      if (params?.typeSearch) {
        countQuery = countQuery.eq("type_id", params.typeSearch);
        dataQuery = dataQuery.eq("type_id", params.typeSearch);
      }

      // Apply date range filters
      if (params?.dateRangeStart) {
        countQuery = countQuery.gte("occurred_at", params.dateRangeStart);
        dataQuery = dataQuery.gte("occurred_at", params.dateRangeStart);
      }

      if (params?.dateRangeEnd) {
        countQuery = countQuery.lte("occurred_at", params.dateRangeEnd);
        dataQuery = dataQuery.lte("occurred_at", params.dateRangeEnd);
      }

      // Handle general search (backward compatibility)
      const searchFilter = params?.search
        ? buildIlikeOrFilter(["title", "body"], params.search)
        : null;
      if (searchFilter) {
        countQuery = countQuery.or(searchFilter);
        dataQuery = dataQuery.or(searchFilter);
      }

      // Contact search: PostgREST filters cannot contain a sub-select, so resolve
      // the matching contact ids first, then filter on them.
      let contactIdFilter: ReturnType<typeof filterIn> = null;
      const contactTerm = params?.contactSearch
        ? buildIlikeOrFilter(["name", "email"], params.contactSearch)
        : null;
      if (contactTerm) {
        const { data: matchedContacts, error: contactError } = await supabase
          .from("contacts")
          .select("id")
          .or(contactTerm)
          .limit(500);
        if (contactError) {
          console.error("Error resolving contact search:", contactError);
          throw new Error(contactError.message);
        }
        contactIdFilter = filterIn(
          "contact_id",
          (matchedContacts ?? []).map((c) => c.id)
        );
      }

      // Handle specific field searches - one OR across the fields searched
      const specificSearchFilter = orFilter([
        params?.titleSearch ? filterSearch("title", params.titleSearch) : null,
        params?.bodySearch ? filterSearch("body", params.bodySearch) : null,
        contactIdFilter,
      ]);
      if (specificSearchFilter) {
        countQuery = countQuery.or(specificSearchFilter);
        dataQuery = dataQuery.or(specificSearchFilter);
      } else if (contactTerm) {
        // The only condition was a contact search that matched no contact.
        return {
          data: [],
          total: 0,
          pageCount: 0,
          pageInfo: { hasNextPage: false, hasPreviousPage: (params?.page || 0) > 0 },
        };
      }

      // Get total count
      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error("Error fetching communications count:", countError);
        throw new Error(countError.message);
      }

      // Apply sorting
      dataQuery = dataQuery.order("occurred_at", { ascending: false });

      // Apply pagination
      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        dataQuery = dataQuery.range(offset, offset + params.limit - 1);
      }

      // Fetch data
      const { data, error } = await dataQuery;
      if (error) {
        console.error("Error fetching communications:", error);
        throw new Error(error.message);
      }

      const total = count || 0;
      const pageCount = params?.limit ? Math.ceil(total / params.limit) : 1;


      return {
        data: (data || []) as Communications[],
        total,
        pageCount,
        pageInfo: {
          hasNextPage: params?.limit ? (params.page || 0) < pageCount - 1 : false,
          hasPreviousPage: (params?.page || 0) > 0,
        },
      };
    },
  });
}
